import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import WaitlistCTA from "./WaitlistCTA.js";

const flow = [
  "Teacher joins Maestro. Skill built for their workflow.",
  "2-8 hours a week of prep time given back.",
  "Teacher trains as a Maestro coach.",
  "Added income. Mission work. Still in the classroom.",
];

const TeacherToCoach = () => {
  const theme = useTheme();

  return (
    <Box component="section" sx={{ py: { xs: 8, md: 14 }, bgcolor: "#fafafa" }}>
      <Container maxWidth="lg">
        <Typography
          sx={{
            display: "inline-block",
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.warning.main,
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Our model
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "2rem", md: "3.25rem" },
            fontWeight: 800,
            letterSpacing: "-0.03em",
            lineHeight: 1.06,
            color: theme.palette.text.primary,
          }}
        >
          Teachers who become{" "}
          <Box component="span" sx={{ color: theme.palette.warning.main }}>
            coaches
          </Box>
          .
        </Typography>

        <Box
          sx={{
            mt: 8,
            bgcolor: theme.palette.paper.primary,
            borderRadius: 3,
            p: { xs: 4, md: 7 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.1fr 1fr" },
            gap: { xs: 5, md: 7.5 },
            alignItems: "center",
            border: `1px solid ${theme.palette.text.tertiary}`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              bgcolor: "#ffedd5",
              opacity: 0.5,
            }}
          />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              component="h3"
              sx={{
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              Give teachers time back. Then{" "}
              <Box component="span" sx={{ color: theme.palette.warning.main }}>
                invest it in them
              </Box>
              .
            </Typography>
            <Typography
              sx={{
                fontSize: "1.0625rem",
                color: theme.palette.text.secondary,
                mt: 2.25,
                lineHeight: 1.55,
              }}
            >
              When Maestro gives you time back, we invest it in you. Teachers
              with capacity become paid Maestro coaches for other teachers in
              the network. It's added income, it's mission work, and it's how we
              keep the best teachers in the classroom instead of losing them to
              admin roles or the exit.
            </Typography>
            <Box
              component="a"
              href="#"
              sx={{
                mt: 3,
                fontSize: "0.9375rem",
                fontWeight: 700,
                color: theme.palette.text.primary,
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                textDecoration: "none",
                "&:hover .arrow": { transform: "translateX(4px)" },
              }}
            >
              Read the deep dive on our coach model{" "}
              <Box
                component="span"
                className="arrow"
                sx={{ transition: "transform 0.15s" }}
              >
                →
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: "relative", zIndex: 1 }}>
            {flow.map((step, i) => (
              <React.Fragment key={i}>
                <Box
                  sx={{
                    p: 2.5,
                    px: 3,
                    bgcolor: "#fafafa",
                    borderRadius: 1.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.25,
                      bgcolor: theme.palette.warning.main,
                      color: theme.palette.paper.primary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "0.9375rem",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.9375rem",
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
                {i < flow.length - 1 && (
                  <Box
                    sx={{
                      textAlign: "center",
                      fontSize: "1.25rem",
                      color: "#d1d5db",
                      my: 0.5,
                    }}
                  >
                    ↓
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>

        <WaitlistCTA />
      </Container>
    </Box>
  );
};

export default TeacherToCoach;
